import React, { useEffect, useRef, useState } from 'react';
import { SlidersHorizontal, GripVertical } from 'lucide-react';

// Mueve `fromKey` justo antes/después de `toKey` dentro de la lista de keys.
const reorderKeys = (keys, fromKey, toKey, pos) => {
  if (fromKey === toKey) return keys;
  const arr = keys.filter((k) => k !== fromKey);
  const idx = arr.indexOf(toKey);
  if (idx === -1) return keys;
  arr.splice(pos === 'after' ? idx + 1 : idx, 0, fromKey);
  return arr;
};

// Botón "Campos" + popover para elegir QUÉ campos se ven y EN QUÉ ORDEN. Cada
// fila se arrastra (drag & drop nativo) para reordenar. La selección y el orden
// son los de la modalidad activa (columnas en tabla, datos del cuerpo en
// tarjetas); la pantalla los gobierna con el hook useFieldLayout.
//
// Props:
//   fields    : [{ key, label }] ocultables, ya en el orden actual.
//   hidden    : array de keys ocultas.
//   onToggle  : (key) => void        alterna la visibilidad de un campo.
//   onReorder : (keys) => void       fija el nuevo orden (array de keys).
//   viewMode  : 'table' | 'cards'    solo para rotular qué se está ordenando.
const FieldPicker = ({ fields = [], hidden = [], onToggle, onReorder, viewMode = 'cards' }) => {
  const [open, setOpen] = useState(false);
  const [dragKey, setDragKey] = useState(null);
  const [over, setOver] = useState({ key: null, pos: null });
  const ref = useRef(null);
  const hiddenSet = new Set(hidden);
  const visibleCount = fields.length - fields.filter((f) => hiddenSet.has(f.key)).length;
  const orderLabel = viewMode === 'table' ? 'Columnas' : 'Campos de la tarjeta';

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!fields.length) return null;

  const clearDrag = () => { setDragKey(null); setOver({ key: null, pos: null }); };

  const handleDrop = (toKey) => {
    if (dragKey && onReorder) {
      const pos = over.key === toKey && over.pos ? over.pos : 'before';
      onReorder(reorderKeys(fields.map((f) => f.key), dragKey, toKey, pos));
    }
    clearDrag();
  };

  return (
    <div className="dt-campos-wrap" ref={ref}>
      <button
        type="button"
        className="btn btn-outline btn-sm dt-campos-btn"
        style={hiddenSet.size > 0 ? { borderColor: 'var(--primary)', color: 'var(--primary)' } : undefined}
        onClick={() => setOpen((o) => !o)}
        title="Elegir y ordenar los campos que se ven"
      >
        <SlidersHorizontal size={15} /> Campos{hiddenSet.size > 0 ? ` (${visibleCount}/${fields.length})` : ''}
      </button>
      {open && (
        <div className="dt-campos-pop" role="menu">
          <div className="dt-campos-head">
            <span className="dt-campos-title">{orderLabel}</span>
            <span className="dt-campos-hint">Arrastra para ordenar</span>
          </div>
          {fields.map((f) => {
            const visible = !hiddenSet.has(f.key);
            const isLastVisible = visible && visibleCount === 1;
            const cls = ['dt-campos-item'];
            if (dragKey === f.key) cls.push('is-dragging');
            if (over.key === f.key && dragKey && dragKey !== f.key) {
              cls.push(over.pos === 'after' ? 'drag-after' : 'drag-before');
            }
            return (
              <div
                key={f.key}
                className={cls.join(' ')}
                draggable
                onDragStart={(e) => {
                  setDragKey(f.key);
                  e.dataTransfer.effectAllowed = 'move';
                  try { e.dataTransfer.setData('text/plain', f.key); } catch (_) { /* noop */ }
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (!dragKey || dragKey === f.key) return;
                  const r = e.currentTarget.getBoundingClientRect();
                  const pos = (e.clientY - r.top) < r.height / 2 ? 'before' : 'after';
                  if (over.key !== f.key || over.pos !== pos) setOver({ key: f.key, pos });
                }}
                onDrop={(e) => { e.preventDefault(); handleDrop(f.key); }}
                onDragEnd={clearDrag}
              >
                <span className="dt-campos-grip" aria-label="Arrastrar para reordenar" title="Arrastrar para reordenar">
                  <GripVertical size={15} />
                </span>
                <label
                  className="dt-campos-label"
                  title={isLastVisible ? 'Debe quedar al menos un campo visible' : undefined}
                >
                  <input
                    type="checkbox"
                    checked={visible}
                    disabled={isLastVisible}
                    onChange={() => onToggle(f.key)}
                  />
                  <span>{f.label}</span>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FieldPicker;
